// Parent product component
if (!customElements.get('component-pdp-gift-card')) {
  customElements.define('component-pdp-gift-card', class PDPGiftCard extends HTMLElement {
    constructor() {
      super();
      // State
      this.state = {
        product: {},
        selectedVariant: '',
        formData: {
          from: '',
          to: '',
          email: '',
          message: '',
          dateMonth: '',
          dateDay: ''
        }
      }
      // Props
      this.props = {
        productHandle: this.dataset.productHandle,
        emailKey: ''
      }
      // DOM
      this.dom = {
        template: this.querySelector('template')
      }
    }
    async connectedCallback() {
      this.state.product = await this.fetchProduct();

      // Set selected variant to first available variant
      this.state.selectedVariant = this.state.product?.variants?.find(variant => !this.isOutOfStock(variant))?.id || '';

      this.dispatchEvent(new CustomEvent('render-product'));

      // Listen for state change from product subcomponents
      this.addEventListener('set-state', (event) => {
        let detail = {};
        if (event.detail && event.detail.selectedVariant) {
          this.state.selectedVariant = event.detail.selectedVariant;
        }
        if (event.detail && event.detail.formData) {
          this.state.formData = event.detail.formData;
        }
        if (event.detail && event.detail.componentsToRender) {
          detail.componentsToRender = event.detail.componentsToRender;
        }
        this.dispatchEvent(new CustomEvent('render', { detail: detail }));
      });
    }
    async fetchProduct() {
      this.dispatchEvent(new CustomEvent('product-loading'));
      // Get product data
      let response = await fetch(`${window.Shopify.routes.root}products/${this.props.productHandle}?view=pdp-gift-card`, {
        method: "get"
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      response = await response.json();
      return response.product;
    }
    getProduct() {
      return this.state.product;
    }
    getVariant() {
      const variants = this.getProduct().variants;
      return variants.find(variant => variant.id === this.state.selectedVariant) || variants[0];
    }
    getSelectedVariant() {
      return this.state.selectedVariant || '';
    }
    isOutOfStock(variant) {
      const currentVariant = variant || this.getVariant();

      if (
        !currentVariant.inventory_management
      ) return false;

      if (
        currentVariant.inventory_management === 'shopify' &&
        currentVariant.inventory_policy === 'continue'
      ) return false;

      if (
        currentVariant.inventory_management === 'shopify' &&
        currentVariant.inventory_policy === 'deny' &&
        currentVariant.available &&
        this.props.outOfStockThresholdProduct &&
        this.props.outOfStockThresholdProduct > currentVariant.inventory_quantity &&
        !this.getProduct().tags.includes('inventory_threshold_ignore')
      ) return false;

      if (
        currentVariant.inventory_management === 'shopify' &&
        currentVariant.inventory_policy === 'deny' &&
        currentVariant.available &&
        this.props.outOfStockThreshold &&
        this.props.outOfStockThreshold > currentVariant.inventory_quantity &&
        !this.getProduct().tags.includes('inventory_threshold_ignore')
      ) return false;

      return true;
    }
  });
}

// Boilerplate class used for all product subcomponents
class PDPProductSubComponent extends HTMLElement {
  constructor() {
    super();

    this.props = {
      hydration: this.dataset.hydration && this.dataset.hydration === 'true' ? true : false
    }

    this.dom = {
      parentComponent: this.closest('component-pdp-gift-card'),
      template: this.querySelector('.js-template'),
      skeletonTemplate: this.querySelector('.js-template-skeleton')
    }

    if (this.props.hydration) {
      const template = document.createElement('template');
      template.content.appendChild(this.dom.template.cloneNode(true).querySelector('.js-container'));
      this.template = template;
    } else {
      this.template = this.dom.template;
    }

    this.dom.parentComponent.addEventListener('render', (event) => {
      // Render all components by default
      let componentsToRender = event.detail?.componentsToRender || 'all';
      // Support ability to re-render only certain components using the component name
      const name = this.tagName.toLowerCase().replace(/-/g, '').replace(/component/g, '');
      const components = componentsToRender.toLowerCase();
      if (components === 'all' || components.includes(name)) {
        if (this.querySelector('.js-container')) {
          this.querySelector('.js-container').remove();
        }
        this.render();
      }
    });
    // Event to render all components after product is fetched
    this.dom.parentComponent.addEventListener('render-product', () => {
      this.render();
    });
  }
  append(template) {
    if (this.querySelector('.js-template') && this.querySelector('.js-template').nodeName === 'DIV') {
      this.querySelector('.js-template').remove();
    }
    this.appendChild(template);
  }
  getProduct() {
    return this.dom.parentComponent.getProduct();
  }
  getVariant() {
    return this.dom.parentComponent.getVariant();
  }
  getSelectedVariant() {
    return this.dom.parentComponent.getSelectedVariant();
  }
  isOutOfStock(variant) {
    return this.dom.parentComponent.isOutOfStock(variant);
  }
}

if (!customElements.get('component-pdp-product-gallery')) {
  customElements.define('component-pdp-product-gallery', class PDPProductGallery extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        firstRender: true,
        mediaIndex: 0,
        models: {},
        activeModelId: ''
      }
      this.props.badgePrefix = this.dataset.badgePrefix;
    }
    async render(modelMedia) {
      const template = this.template.content.cloneNode(true);
      // Reset media index to show first image on color change
      this.state.mediaIndex = 0;
      if (!modelMedia) {
        this.state.activeModelId = '';
      }
      // Default image size matches size in 'json-color' snippet
      this.defaultImgSize = '300';
      this.state.media = [this.getVariant().featured_image];
      if (this.props.hydration && !this.state.firstRender) {
        if (window.innerWidth < 768) {
          this.renderMobileGallery(template);
        } else {
          this.renderDesktopGallery(template);
        }
        this.append(template);
      }
      const badge = this.getBadge();
      if (badge) {
        this.renderBadge(this, badge);
      }
      this.afterRender();
    }
    renderDesktopGallery(template) {
      this.renderMedia(template);
    }
    renderMobileGallery(template) {
      this.renderMedia(template, true);
    }
    renderMedia(template, isMobile) {
      if (template.querySelector('.js-media') && !isMobile) template.querySelector('.js-media').remove();
      if (template.querySelector('.js-gallery-mobile') && isMobile) template.querySelectorAll('.js-gallery-mobile > *:not(template)').forEach(item => item.remove());
      let mediaEl;
      const media = this.state.media[this.state.mediaIndex];
      if (!media) return;
      if (media.type === 'video' && media.videoSrc) {
        mediaEl = document.createElement('video');
        const sourceEl = document.createElement('source');
        mediaEl.controls = true;
        mediaEl.playsinline = true;
        sourceEl.src = media.videoSrc;
        sourceEl.type = `video/${media.videoFormat}`;
        mediaEl.appendChild(sourceEl);
      } else {
        mediaEl = document.createElement('img');
        mediaEl.dataset.srcset = getSrcset(media.src, this.defaultImgSize);
        mediaEl.dataset.sizes = isMobile ? '100vw' : 'auto';
        mediaEl.classList.add('lazyload');
        mediaEl.width = media.width;
        mediaEl.height = media.height;
        mediaEl.alt = media.alt;
      }
      if (isMobile) {
        mediaEl.classList.add('mobile-media', 'js-mobile-media');
        mediaEl.dataset.index = '0';
        mediaEl.classList.add(`js-mobile-media-0`);
        template.querySelector('.js-gallery-mobile').appendChild(mediaEl);
      } else {
        mediaEl.classList.add('js-media', 'media');
        template.querySelector('.js-media-container').appendChild(mediaEl);
      }
    }
    getBadge() {
      const tags = this.getProduct().tags;
      let badge;
      for (let tag in tags) {
        if (typeof tags[tag] === 'string' && tags[tag].includes(this.props.badgePrefix)) {
          badge = tags[tag].replace(this.props.badgePrefix, '');
          break;
        }
      }
      return badge;
    }
    renderBadge(template, badge) {
      function updateBadge() {
        const badgeTemplate = template.querySelector('.js-template-gallery-badge').content.cloneNode(true);
        badgeTemplate.querySelector('.js-gallery-badge').innerHTML = badge;
        return badgeTemplate;
      }
      template.querySelector('.js-gallery-desktop-container .js-media-container').append(updateBadge());
      template.querySelector('.js-gallery-mobile-container .js-gallery-mobile').append(updateBadge());
    }
    handleStylistChat() {
      if (this.querySelector('.js-chat-stylist')) {
        this.querySelector('.js-chat-stylist').addEventListener('click', () => {
          document.querySelector('[data-chat-action="openChat"]').click();
        });
      }
      if (window.theme?.chat?.isOnline) {
        if (this.querySelector('.js-chat-stylist-container')) {
          this.querySelector('.js-chat-stylist-container').classList.remove('hidden');
        }
      }
    }
    afterRender() {
      this.state.firstRender = false;
      this.handleStylistChat();
      if (window.theme?.loyalty?.is_rewards_banner_displayed) {
        const parentNode = this.parentNode;
        if (parentNode) parentNode.style.top = '213px';
      }
    }
  })
}

// Product info subcomponent
if (!customElements.get('component-pdp-product-info')) {
  customElements.define('component-pdp-product-info', class PDPProductInfo extends PDPProductSubComponent {
    constructor() {
      super();
      this.props = {
        bdEnabled: this.dataset.bdEnabled === 'true',
        showPriceBadge: this.dataset.showPriceBadge,
        priceBadgeSetsTag: this.dataset.priceBadgeSetsTag
      }
      this.state = {};
    }
    render() {
      const template = this.template.content.cloneNode(true);

      // Set eyebrow and title
      const product = this.getProduct();
      if (product?.eyebrow) {
        // Set eyebrow text
        template.querySelector('.js-eyebrow').textContent = product.eyebrow;
        // Set eyebrow collection link
        try {
          const eyebrowCollections = document.querySelector('.js-eyebrow-collections');
          if (eyebrowCollections) {
            const jsonObj = JSON.parse(eyebrowCollections.innerHTML);
            if (Object.keys(jsonObj).length !== 0) {
              const eyebrowKey = Object.keys(jsonObj).find(key => key === product.eyebrow.toLowerCase().trim());
              if (jsonObj[eyebrowKey]) {
                template.querySelector('.js-eyebrow-link').href = jsonObj[eyebrowKey];
              } else {
                template.querySelector('.js-eyebrow-link').removeAttribute('href');
              }
            }
          }
        } catch (e) {
          console.error('Failed to parse eyebrow collections JSON:', e);
        }
      }

      // Set pricing
      if (product) {
        template.querySelector('.js-price').textContent = toMoney(this.getVariant().price);
        // Afterpay
        if (template.querySelector('.js-afterpay')) {
          if ((price) > parseFloat(template.querySelector('.js-afterpay').dataset.minPrice)) {
            template.querySelector('.js-afterpay-price').innerHTML = toMoney(price / parseInt(template.querySelector('.js-afterpay-available').dataset.installments));
            template.querySelector('.js-afterpay-available').classList.remove('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.add('hidden');
          } else {
            template.querySelector('.js-afterpay-available').classList.add('hidden');
            template.querySelector('.js-afterpay-unavailable').classList.remove('hidden');
          }
        }
      }
      if (this.props.hydration && this.querySelector('.js-template')) this.querySelector('.js-template').remove();
      this.append(template);
    }
  });
}

// Product variants subcomponent
if (!customElements.get('component-pdp-product-variants-gift-card')) {
  customElements.define('component-pdp-product-variants-gift-card', class PDPProductVariantsGiftCard extends PDPProductSubComponent {
    constructor() {
      super();

      this.props.isLanding = this.dataset.isLanding === 'true';
      this.props.inventoryQuantityThreshold = this.dataset.inventoryQuantityThreshold;
      this.props.inventoryQuantityMessage = this.dataset.inventoryQuantityMessage;
      this.props.outOfStockThreshold = this.dataset.outOfStockThreshold || 0;
      this.props.outOfStockThresholdProduct = this.dataset.outOfStockThresholdProduct || 0;
      this.props.variantDropdownDefaultLabel = this.dataset.variantDropdownDefaultLabel || 'Variant';

      this.state = {
        variant: null
      };

      this.isCanada = window.Shopify.country === 'CA';
    }
    render() {
      const template = this.template.content.cloneNode(true);
      this.selectTypeButton(template);
      this.renderVariantsColors(template);
      this.renderVariantsAmount(template);
      this.hideFormInputs(template);
      this.renderOutOfStock(template);
      if (this.querySelector('.js-skeleton')) this.querySelector('.js-skeleton').remove();
      this.append(template);
      this.afterRender();
    }
    selectTypeButton(template) {
      if (this.getProduct()?.subtype?.toLowerCase().replaceAll(' ', '-') === 'e-gift-card') {
        template.querySelector('.js-e-gift-card')?.classList.add('selected');
        template.querySelector('.js-gift-card')?.classList.remove('selected');
      } else {
        template.querySelector('.js-gift-card')?.classList.remove('selected');
        template.querySelector('.js-gift-card')?.classList.add('selected');
      }
    }
    renderVariantsColors(template) {
      const allColors = window.theme.colors || {};
      const currentVariant = this.getVariant();
      const colorVariants = this.getProduct().variants.filter(variant => variant.option2 === currentVariant.option2);
      colorVariants.forEach(colorVariant => {
        const color = colorVariant.option1.toLowerCase().replaceAll(' ', '-');
        const colorTemplate = template.querySelector('.js-template-variant-color').content.cloneNode(true);
        // Set CSS custom properties for swatch styling
        if (allColors[color]?.image) {
          colorTemplate.querySelector('.js-label').style.setProperty('--swatch-image', `url(${allColors[color].image}`);
        }
        colorTemplate.querySelector('.js-label').style.setProperty('--swatch-color', allColors[color]?.color || '#FFFFFF');
        colorTemplate.querySelector('.js-label').style.setProperty('--swatch-border-color', '#FFFFFF');
        colorTemplate.querySelector('.js-label').setAttribute('for', `color_${color}`);
        colorTemplate.querySelector('.js-input').value = color;
        colorTemplate.querySelector('.js-input').dataset.variant = colorVariant.id;
        colorTemplate.querySelector('.js-input').id = `color_${color}`;
        const currentColor = currentVariant?.option1?.toLowerCase().replaceAll(' ', '-');
        if (currentColor === color) {
          colorTemplate.querySelector('.js-input').checked = true;
          const colorTitle = window.theme.colors[color]?.title;
          if (colorTitle) template.querySelector(`.js-active-color`).innerHTML = colorTitle;
        }
        template.querySelector(`.js-variants-color`).appendChild(colorTemplate);
      });
      return;
    }
    renderVariantsAmount(template) {
      const variantTemplate = template.querySelector('.js-template-variant-amount').content.cloneNode(true);
      const defaultOptionTemplate = variantTemplate.querySelector('.js-template-variant-amount-option').content.cloneNode(true);
      const defaultOption = defaultOptionTemplate.querySelector('.js-variant-option');
      defaultOption.innerHTML = this.props.variantDropdownDefaultLabel;
      defaultOption.disabled = true;
      defaultOption.selected = true;
      variantTemplate.querySelector('.js-variant-dropdown').appendChild(defaultOptionTemplate);
      const variants = this.getProduct().variants;
      const selectedColor = this.getVariant()?.option1?.toLowerCase().replaceAll(' ', '-');
      variants.forEach(variant => {
        const variantColor = variant.option1.toLowerCase().replaceAll(' ', '-');
        if (selectedColor !== variantColor) return;
        const optionTemplate = variantTemplate.querySelector('.js-template-variant-amount-option').content.cloneNode(true);
        if (this.isOutOfStock(variant)) {
          optionTemplate.querySelector('.js-variant-option').textContent = `${toMoney(variant.price)} (not available)`;
          optionTemplate.querySelector('.js-variant-option').disabled = true;
        } else {
          optionTemplate.querySelector('.js-variant-option').textContent = toMoney(variant.price);
        }
        optionTemplate.querySelector('.js-variant-option').dataset.variant = variant.id;
        // Set active variant from state
        if (variant.id === this.getSelectedVariant()) {
          optionTemplate.querySelector('.js-variant-option').selected = true;
        }
        // Add option to options list
        variantTemplate.querySelector('.js-variant-dropdown').appendChild(optionTemplate);
      });
      template.querySelector('.js-variants-amount').appendChild(variantTemplate);
    }
    hideFormInputs(template) {
      if (this.getProduct()?.subtype?.toLowerCase().replaceAll(' ', '-') !== 'e-gift-card') {
        template.getElementById('from-to-container')?.classList.add('hidden');
        template.getElementById('email-container')?.classList.add('hidden');
        template.getElementById('message-container')?.classList.add('hidden');
        template.getElementById('date-container')?.classList.add('hidden');
      }
    }
    renderOutOfStock(template) {
      if (this.isOutOfStock() || this.getProduct().tags.includes('__label: Coming Soon')) {
        // If variant is unavailable, show back in stock widget or message
        let showBIS = true;
        if (this.getProduct().tags.includes('bis-hidden') || this.getProduct().tags.includes('bis-paused')) {
          showBIS = false;
        }
        if(this.getProduct().tags.includes('__label: Coming Soon')) {
          showBIS = true;
        }
        if (showBIS && template.querySelector('.js-back-in-stock') && this.getVariant()) {
          template.querySelector('.js-out-of-stock').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = true;
          template.querySelector('.js-add-to-bag').classList.add('hidden');
          const backInStockTemplate = template.querySelector('.js-template-back-in-stock').content.cloneNode(true);
          backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.productId = this.getProduct().id;
          backInStockTemplate.querySelector('component-pdp-product-back-in-stock').dataset.variantId = this.getVariant().id;
          template.querySelector('.js-back-in-stock').appendChild(backInStockTemplate);
        } else {
          template.querySelector('.js-out-of-stock').classList.remove('hidden');
          template.querySelector('.js-add-to-bag').disabled = true;
        }
      } else {
        template.querySelector('.js-out-of-stock').classList.add('hidden');
        template.querySelector('.js-add-to-bag').disabled = false;
        template.querySelector('.js-add-to-bag').innerHTML = template.querySelector('.js-add-to-bag').dataset.addToBagText;
      }
    }
    renderInventoryMessage(template) {
      if (this.getVariant() && !this.isOutOfStock() && this.getVariant().inventory_management === 'shopify' && this.getVariant().inventory_policy === 'deny' && Number(this.getVariant().inventory_quantity) < Number(this.props.inventoryQuantityThreshold)) {
        template.querySelector('.js-inventory-message').textContent = this.props.inventoryQuantityMessage.replace('#', this.getVariant().inventory_quantity);
        template.querySelector('.js-inventory-message').classList.remove('hidden');
      } else {
        template.querySelector('.js-inventory-message').classList.add('hidden');
      }
      if (this.getProduct().tags.includes('__label: Coming Soon')) {
        template.querySelector('.js-inventory-message').classList.add('hidden');
      }
    }
    renderFinalSale(template) {
      if (!this.isOutOfStock() && isFinalSale(this.getProduct().tags)) {
        template.querySelector('.js-final-sale').classList.remove('hidden');
      } else {
        template.querySelector('.js-final-sale').classList.add('hidden');
      }
    }
    updateLoyaltyBanner() {
      document.dispatchEvent(new CustomEvent('pdp-loyalty-banner-updated', { detail: { price: this.getVariant()?.price || this.getProduct()?.price * 100 }}));
    }
    afterRender() {
      if (document.querySelector('.js-variants-accordions-container')) {
        document.querySelector('.js-variants-accordions-container').classList.remove('hidden');
      }
      this.handleVariantColorChange();
      this.handleVariantAmountChange();
      this.populateForm();
      this.handleTypeButtons();
      this.handleAddToBag();
    }
    handleVariantColorChange() {
      this.querySelectorAll('.js-variants-color .js-input').forEach(color => {
        color.addEventListener('change', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const variantId = +color.dataset.variant;
          this.state.variant = variantId;
          // Update selected variant state and rerender components
          const detail = {
            componentsToRender: 'PDPProductVariantsGiftCard, PDPProductInfo, PDPProductGallery',
            formData: {
              from: document.getElementById('form-input-from').value,
              to: document.getElementById('form-input-to').value,
              email: document.getElementById('form-input-email').value,
              message: document.getElementById('form-input-message').value,
              dateMonth: document.getElementById('form-input-date-month').value,
              dateDay: document.getElementById('form-input-date-day').value
            }
          }
          if (variantId) detail.selectedVariant = variantId;
          this.dispatchEvent(new CustomEvent('set-state', {
            bubbles: true,
            detail: detail
          }));
          if (variantId) {
            this.sendVariantChangeGTMData('color');
            this.updateLoyaltyBanner();
          }
        });
      });
    }
    handleVariantAmountChange() {
      this.querySelectorAll('.js-variant-amount').forEach(group => {
        const dropdown = group.querySelector('.js-variant-dropdown');
        dropdown.addEventListener('change', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantOption = dropdown.options[dropdown.selectedIndex];
          const variantId = +variantOption.dataset.variant;
          this.state.variant = variantId;
          // Update selected variant state and rerender components
          const detail = {
            componentsToRender: 'PDPProductVariantsGiftCard, PDPProductInfo, PDPProductGallery',
            formData: {
              from: document.getElementById('form-input-from').value,
              to: document.getElementById('form-input-to').value,
              email: document.getElementById('form-input-email').value,
              message: document.getElementById('form-input-message').value,
              dateMonth: document.getElementById('form-input-date-month').value,
              dateDay: document.getElementById('form-input-date-day').value
            }
          }
          if (variantId) detail.selectedVariant = variantId;
          this.dispatchEvent(new CustomEvent('set-state', {
            bubbles: true,
            detail: detail
          }));
          if (variantId) {
            this.sendVariantChangeGTMData('amount');
            this.updateLoyaltyBanner();
          }
        });
      });
    }
    populateForm() {
      document.getElementById('form-input-from').value = this.dom.parentComponent.state.formData.from || '';
      document.getElementById('form-input-to').value = this.dom.parentComponent.state.formData.to || '';
      document.getElementById('form-input-email').value = this.dom.parentComponent.state.formData.email || '';
      document.getElementById('form-input-message').value = this.dom.parentComponent.state.formData.message || '';
      document.getElementById('form-input-date-month').value = this.dom.parentComponent.state.formData.dateMonth || '';
      document.getElementById('form-input-date-day').value = this.dom.parentComponent.state.formData.dateDay || '';
    }
    handleTypeButtons() {
      this.querySelectorAll('.js-type-buttons button').forEach(button => {
        button.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (e.target.classList.contains('selected')) return;

          const subtype = e.target.dataset.subtype;
          if (subtype === 'e-gift-card') {
            this.querySelector('.js-e-gift-card').classList.add('selected');
            this.querySelector('.js-gift-card').classList.remove('selected');
          } else {
            this.querySelector('.js-e-gift-card').classList.remove('selected');
            this.querySelector('.js-gift-card').classList.add('selected');
          }

          const handle = e.target.dataset.handle;
          window.location.href = `/products/${handle}`;
        });
      })
    }
    async handleAddToBag() {
      const addTobagButton = this.querySelector('.js-add-to-bag');
      addTobagButton.addEventListener('click', async (event) => {
        event.preventDefault();

        try {
          // Prevent multiple clicks
          if (addTobagButton.classList.contains('add-to-bag-btn--loading')) {
            return;
          }

          const product = this.getProduct();
          const variant = this.getVariant();

          if (!variant) {
            throw new Error('No variant selected');
          }

          addTobagButton.innerHTML = addTobagButton.dataset.loadingText;
          addTobagButton.classList.add('add-to-bag-btn--loading');

          const formInputFromEl = document.getElementById('form-input-from');
          const formInputToEl = document.getElementById('form-input-to');
          const formInputEmailEl = document.getElementById('form-input-email');
          const formInputMessageEl = document.getElementById('form-input-message');
          const formInputDateMonthEl = document.getElementById('form-input-date-month');
          const formInputDateDayEl = document.getElementById('form-input-date-day');

          if (product.subtype?.toLowerCase().replaceAll(' ', '-') === 'e-gift-card') {
            if (!formInputFromEl?.value) {
              formInputFromEl.classList.add('input-error');
              throw 'NO_SENDER_NAME';
            } else if (formInputFromEl?.value && formInputFromEl.value.length < 2) {
              formInputFromEl.classList.add('input-error');
              throw 'INVALID_SENDER_NAME';
            } else {
              formInputFromEl.classList.remove('input-error');
              formInputToEl.classList.remove('input-error');
              formInputEmailEl.classList.remove('input-error');
              formInputMessageEl.classList.remove('input-error');
              formInputDateMonthEl.classList.remove('input-error');
              formInputDateDayEl.classList.remove('input-error');
            }

            if (!formInputToEl?.value) {
              formInputToEl.classList.add('input-error');
              throw 'NO_RECIPIENT_NAME';
            } else if (formInputToEl?.value && formInputToEl.value.length < 2) {
              formInputToEl.classList.add('input-error');
              throw 'INVALID_RECIPIENT_NAME';
            } else {
              formInputToEl.classList.remove('input-error');
              formInputEmailEl.classList.remove('input-error');
              formInputMessageEl.classList.remove('input-error');
              formInputDateMonthEl.classList.remove('input-error');
              formInputDateDayEl.classList.remove('input-error');
            }

            if (formInputMessageEl?.value && formInputMessageEl.value.length < 3 || formInputMessageEl.value.length > 250) {
              formInputMessageEl.classList.add('input-error');
              throw 'INVALID_MESSAGE';
            } else {
              formInputMessageEl.classList.remove('input-error');
              formInputDateMonthEl.classList.remove('input-error');
              formInputDateDayEl.classList.remove('input-error');
            }

            if (formInputEmailEl?.value && !String(formInputEmailEl.value).toLowerCase().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)) {
              formInputEmailEl.classList.add('input-error');
              throw 'INVALID_RECIPIENT_EMAIL';
            } else {
              formInputEmailEl.classList.remove('input-error');
              formInputMessageEl.classList.remove('input-error');
              formInputDateMonthEl.classList.remove('input-error');
              formInputDateDayEl.classList.remove('input-error');
            }

            if (formInputEmailEl?.value) {
              const today = new Date();
              if (formInputDateMonthEl?.value && (isNaN(formInputDateMonthEl.value) || formInputDateMonthEl.value < 1 || formInputDateMonthEl.value > 12)) {
                formInputDateMonthEl.classList.add('input-error');
                throw 'INVALID_MONTH';
              } else if (formInputDateMonthEl?.value && !formInputDateDayEl?.value) {
                formInputDateDayEl.classList.add('input-error');
                throw 'NO_DAY';
              } else if (!formInputDateMonthEl?.value && formInputDateDayEl?.value) {
                formInputDateDayEl.classList.add('input-error');
                throw 'NO_MONTH';
              } else {
                formInputDateMonthEl.classList.remove('input-error');
                formInputDateDayEl.classList.remove('input-error');
              }

              if (formInputDateDayEl?.value && (isNaN(formInputDateDayEl.value) || formInputDateDayEl.value < 1 || formInputDateDayEl.value > 31)) {
                formInputDateDayEl.classList.add('input-error');
                throw 'INVALID_DAY';
              } else {
                formInputDateDayEl.classList.remove('input-error');
              }

              if (formInputDateMonthEl?.value && formInputDateDayEl?.value) {
                const futureLimitDate = new Date(new Date().setDate(today.getDate() + 90));
                let sendOnDate = null;
                if (futureLimitDate.getFullYear() != today.getFullYear() && formInputDateMonthEl.value < 6) {
                  sendOnDate = new Date(futureLimitDate.getFullYear(), formInputDateMonthEl.value - 1, formInputDateDayEl.value);
                } else {
                  sendOnDate = new Date(today.getFullYear(), formInputDateMonthEl.value - 1, formInputDateDayEl.value);
                }
                if (sendOnDate === 'Invalid Date' || sendOnDate.getMonth() !== +formInputDateMonthEl.value - 1 || sendOnDate.getDate() !== +formInputDateDayEl.value) {
                  formInputDateDayEl.classList.add('input-error');
                  throw 'INVALID_DAY';
                } else if (sendOnDate < today && sendOnDate.getDate() !== today.getDate()) {
                  formInputDateMonthEl.classList.add('input-error');
                  formInputDateDayEl.classList.add('input-error');
                  throw 'FUTURE_DATE_ERROR_1';
                }

                if (sendOnDate < today && sendOnDate.getDate() === today.getDate()) {
                  formInputDateMonthEl.value = '';
                  formInputDateDayEl.value = '';
                }

                if (sendOnDate > futureLimitDate) {
                  formInputDateMonthEl.classList.add('input-error');
                  formInputDateDayEl.classList.add('input-error');
                  throw 'FUTURE_DATE_ERROR_2';
                }
              }
            } else {
              formInputDateMonthEl.value = '';
              formInputDateDayEl.value = '';
            }
          }

          this.querySelector('.js-add-to-bag-error').classList.add('hidden');

          const subtype = product.subtype?.toLowerCase().replaceAll(' ', '-');
          const isEGiftCard = subtype === 'e-gift-card';
          const payload = {
            id: variant.id,
            quantity: 1,
            properties: {
              _tags: filterProductTags(product?.tags),
              _type: product.type,
              _subtype: subtype,
              _sku: variant.sku,
              _amount: variant.option2,
              _design: variant.option1.toLowerCase().trim().replaceAll(' ', '-')
            }
          };

          const checkoutGiftCardLabel = window.cart_settings.checkoutGiftCardLabel;
          if (checkoutGiftCardLabel) {
            payload.properties['Gift Card'] = checkoutGiftCardLabel;
          }

          if (isEGiftCard) {
            payload.properties._senderName = formInputFromEl.value;
            payload.properties['Recipient name'] = formInputToEl.value;
            payload.properties._recipientName = formInputToEl.value;
            if (formInputEmailEl.value) {
              payload.properties['__shopify_send_gift_card_to_recipient'] = true;
              payload.properties['Recipient email'] = formInputEmailEl.value;
              payload.properties._recipientEmail = formInputEmailEl.value;
              if (formInputDateMonthEl.value && formInputDateDayEl.value) {
                const today = new Date();
                const futureLimitDate = new Date(new Date().setDate(today.getDate() + 90));
                let sendOnDate = null;
                if (futureLimitDate.getFullYear() != today.getFullYear() && formInputDateMonthEl.value < 6) {
                  sendOnDate = new Date(`${ futureLimitDate.getFullYear()} / ${ formInputDateMonthEl.value } / ${ formInputDateDayEl.value }`).toISOString().split('T')[0];
                } else {
                  sendOnDate = new Date(`${ today.getFullYear()} / ${ formInputDateMonthEl.value } / ${ formInputDateDayEl.value }`).toISOString().split('T')[0];
                }
                payload.properties['Send on'] = sendOnDate;
                payload.properties._sendOn = sendOnDate;
              }
            }
            if (formInputMessageEl.value) {
              payload.properties['Message'] = formInputMessageEl.value;
              payload.properties._message = formInputMessageEl.value;
            }
          }

          await window.cartManager.addItem(payload);

          window.broadcastChannel.dispatchEvent('update-cart');

          this.dispatchEvent(new CustomEvent('set-state', {
            bubbles: true,
            detail: {
              componentsToRender: 'PDPProductVariantsGiftCard, PDPProductInfo, PDPProductGallery',
              selectedVariant: this.getSelectedVariant(),
              formData: {
                from: '',
                to: '',
                email: '',
                message: '',
                dateMonth: '',
                dateDay: ''
              }
            }
          }));
        } catch (error) {
          addTobagButton.classList.remove('add-to-bag-btn--loading');
          addTobagButton.innerHTML = addTobagButton.dataset.addToBagText;
          let errorMsg;
          if (!this.getSelectedVariant()) {
            errorMsg = addTobagButton.dataset.variantError;
          } else if (error === 'NO_SENDER_NAME') {
            errorMsg = 'Please enter a sender name';
          } else if (error === 'INVALID_SENDER_NAME') {
            errorMsg = 'Please enter a valid sender name';
          } else if (error === 'NO_RECIPIENT_NAME') {
            errorMsg = 'Please enter a recipient name';
          } else if (error === 'INVALID_RECIPIENT_NAME') {
            errorMsg = 'Please enter a valid recipient name';
          } else if (error === 'INVALID_MESSAGE') {
            errorMsg = 'Please enter a message within 3 and 200 characters';
          } else if (error === 'INVALID_RECIPIENT_EMAIL') {
            errorMsg = 'Please enter a valid recipient’s email';
          } else if (error === 'INVALID_MONTH') {
            errorMsg = 'Please enter a valid month';
          } else if (error === 'INVALID_DAY') {
            errorMsg = 'Please enter a valid day';
          } else if (error === 'NO_MONTH') {
            errorMsg = 'Please enter a month';
          } else if (error === 'NO_DAY') {
            errorMsg = 'Please enter a day';
          } else if (error === 'FUTURE_DATE_ERROR_1') {
            errorMsg = 'Please enter a date in the future';
          } else if (error === 'FUTURE_DATE_ERROR_2') {
            errorMsg = 'Please enter a date within 90 days from now';
          } else if (addTobagButton.dataset.cartError) {
            errorMsg = addTobagButton.dataset.cartError;
          } else {
            errorMsg = 'Something went wrong. Please try again';
          }
          this.querySelector('.js-add-to-bag-error').innerHTML = errorMsg;
          this.querySelector('.js-add-to-bag-error').classList.remove('hidden');
        }
      });
    }
    sendVariantChangeGTMData(option) {
      window.sendGTMData && window.sendGTMData({
        event: 'pdp_gift_card_variant_selected',
        product_id: this.getProduct().id,
        variant_selected: this.getVariant().id,
        color_selected: this.getVariant().option1.toLowerCase().replaceAll(' ', '-'),
        amount_selected: this.getVariant().option2,
        option_selected: option,
        source: 'pdp'
      });
    }
  });
}

// Product details accordion subcomponent
if (!customElements.get('component-pdp-product-accordion')) {
  customElements.define('component-pdp-product-accordion', class PDPProductAccordion extends PDPProductSubComponent {
    constructor() {
      super();
      this.state = {
        expanded: this.dataset.expanded === 'true'
      }
      this.props.type = this.dataset.type;
      this.props.showProductDetailsIcons = this.dataset.showProductDetailsIcons === 'true';
    }
    render() {
      const template = this.template.content.cloneNode(true);
      const icon = document.createElement("span");
      if (this.state.expanded) {
        template.querySelector('.js-accordion-details').open = true;
      }
      icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.21967 9.21967C7.51256 8.92678 7.98744 8.92678 8.28033 9.21967L11.75 12.6893L15.2197 9.21967C15.5126 8.92678 15.9874 8.92678 16.2803 9.21967C16.5732 9.51256 16.5732 9.98744 16.2803 10.2803L12.2803 14.2803C12.1397 14.421 11.9489 14.5 11.75 14.5C11.5511 14.5 11.3603 14.421 11.2197 14.2803L7.21967 10.2803C6.92678 9.98744 6.92678 9.51256 7.21967 9.21967Z" fill="#2C272D"></path></svg>';
      if (template.querySelector('.js-accordion-icon')) template.querySelector('.js-accordion-icon').appendChild(icon);
      let accordionContent;
      if (this.props.type === "product_details") {
        // Render product detail icons
        if (this.props.showProductDetailsIcons && this.getProduct().product_details_icons) {
          template.querySelectorAll('.js-product-details-icons > *:not(template)').forEach(item => item.remove());
          for (let icon of this.getProduct().product_details_icons) {
            if (icon.image && icon.text) {
              const iconTemplate = template.querySelector('.js-template-product-detail-icon').content.cloneNode(true);
              iconTemplate.querySelector('.js-product-detail-img').dataset.src = icon.image.src;
              iconTemplate.querySelector('.js-product-detail-img').dataset.sizes = 'auto';
              iconTemplate.querySelector('.js-product-detail-img').width = icon.image.width;
              iconTemplate.querySelector('.js-product-detail-img').height = icon.image.height;
              iconTemplate.querySelector('.js-product-detail-img').classList.add('lazyload');
              iconTemplate.querySelector('.js-product-details-icon-text').innerHTML = icon.text;
              template.querySelector('.js-product-details-icons').appendChild(iconTemplate);
            }
          }
        }
        if (this.getProduct()?.accordions?.product_details) {
          accordionContent = this.getProduct().accordions.product_details;
        } else {
          accordionContent = this.getProduct().description;
        }
      } else if (this.props.type === "size_fit") {
        accordionContent = this.getProduct()?.accordions?.size_fit;
      } else if (this.props.type === "fabric_care") {
        accordionContent = this.getProduct()?.accordions?.fabric_care;
      } else {
        accordionContent = template.querySelector('.js-accordion-content') ? template.querySelector('.js-accordion-content').innerHTML : null;
      }
      if (accordionContent) {
        template.querySelector('.js-accordion-content').innerHTML = accordionContent;
      } else {
        return;
      }
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-accordion-details').addEventListener('click', () => {
        this.state.expanded = !this.state.expanded;
        this.sendAccordionGTMData();
      });
    }
    sendAccordionGTMData() {
      window.sendGTMData && window.sendGTMData({
        event: `pdp_accordion_${this.props.type}`,
        product_id: this.getProduct().id,
        expanded: this.state.expanded,
        source: 'pdp'
      });
    }
  });
}

// Back in stock component
if (!customElements.get('component-pdp-product-back-in-stock')) {
  customElements.define('component-pdp-product-back-in-stock', class PDPProductBackInStock extends HTMLElement {
    constructor() {
      super();
      this.state = {
        acceptsMarketing: this.dataset.bisKlaviyoEnabled === 'true',
        email: ''
      }
      this.props = {
        variantId: this.dataset.variantId,
        productId: this.dataset.productId,
        disServiceKey: JSON.parse(this.dataset.pskey).disServiceKey,
        cryptoSecret: this.dataset.cryptoSecret,
        bisKlaviyoEnabled: this.dataset.bisKlaviyoEnabled === 'true',
        bisKlaviyoPublicKey: this.dataset.bisKlaviyoPublicKey || '',
        bisKlaviyoRevision: this.dataset.bisKlaviyoRevision || '2024-10-15',
        bisKlaviyoListId: this.dataset.bisKlaviyoListId || ''
      }
      this.render();
    }
    render() {
      const template = this.querySelector('template').content.cloneNode(true);
      this.append(template);
      this.afterRender();
    }
    afterRender() {
      this.querySelector('.js-email-input').addEventListener('input', input => {
        this.state.email = input.srcElement.value;
      });
      this.querySelector('.js-subscribe-checkbox').addEventListener('change', checkbox => {
        this.state.acceptsMarketing = checkbox.srcElement.checked;
      });
      this.querySelector('.js-form').addEventListener('submit', async (event) => {
        event.preventDefault();

        // Identify user on Klaviyo
        window.klaviyoIdentify && window.klaviyoIdentify(this.state.email);

        if (this.state.acceptsMarketing) {
          await this.registerProspect();
        }
        if (this.props.bisKlaviyoEnabled) {
          if (this.props.bisKlaviyoListId) {
            await this.registerToNewsletterInKlaviyo(this.state.email, this.props.bisKlaviyoListId);
          }
          await this.registerBackInStockInKlaviyo(this.state.email);
        } else {
          await this.registerBackInStock();
        }
      });
    }
    async sendEmailSubscribeGTMEvent() {
      await import(document.querySelector('.js-component-pdp-product').dataset.browserCrypto);

      const cipherInstance = browserCrypto.createCipher(
        'aes-256-cbc',
        this.props.cryptoSecret,
      );
      const encryptedEmail = cipherInstance.update(this.state.email, 'utf8', 'hex');
      const finalEmailHash = encryptedEmail + cipherInstance.final('hex');

      window.sendGTMData && window.sendGTMData({
        event: 'email_subscribe',
        email: finalEmailHash,
        source: 'pdp_capture_back_in_stock'
      });
    }
    async registerProspect() {
      try {
        const data = {
          email: this.state.email,
          source: 'pdp_capture',
          user_locale: window.userLocale
        };
        const url = window.location.hostname.includes('trythirdlove.com') ? 'https://dis-apis.trythirdlove.com/v1/prospects/registration' : 'https://dis-apis.thirdlove.com/v1/prospects/registration';
        let response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.props.disServiceKey,
          },
          method: 'POST',
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (errDis) {
        console.error('Error while sending new prospect to DIS', errDis);
      }
    }
    async registerToNewsletterInKlaviyo(email, listId) {
      try {
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId
                }
              }
            }
          }
        };

        const options = {
          method: 'POST',
          headers: {revision: this.props.bisKlaviyoRevision, 'content-type': 'application/vnd.api+json'},
          body: JSON.stringify(payload)
        };

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, options);
        if (!response.ok) {
          const jsonResponse = await response.json();
          throw new Error(jsonResponse);
        }
      } catch (err) {
        console.error('Error subscribing user to the newsletter: ', JSON.stringify(err));
      }
    }
    async registerBackInStock() {
      let response;
      try {
        response = await BIS.create(this.state.email, this.props.variantId, this.props.productId, {
          accepts_marketing: this.state.acceptsMarketing,
          quantity_required: 1,
          customer_utc_offset: new Date().getTimezoneOffset()
        });
        if (response.status !== 'OK' && !response.errors?.base[0]?.includes('already registered')) {
          throw new Error(response);
        } else {
          if (this.state.acceptsMarketing) {
            await this.sendEmailSubscribeGTMEvent();
          }
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (errBis) {
        console.error('Error while sending information to BIS App', errBis?.errors?.base);
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
    async registerBackInStockInKlaviyo(email) {
      try {
        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.props.bisKlaviyoPublicKey}`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/vnd.api+json',
            'revision': this.props.bisKlaviyoRevision
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['email'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email
                    }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${this.props.variantId}`
                  }
                }
              }
            }
          })
        });
        if (!response.ok) {
          throw new Error(response);
        } else {
          this.querySelector('.js-content').classList.add('hidden');
          this.querySelector('.js-error-msg').classList.add('hidden');
          this.querySelector('.js-success-msg').classList.remove('hidden');
        }
      } catch (err) {
        console.error('Back in stock subscription error: ', err);
        this.querySelector('.js-error-msg').classList.remove('hidden');
        this.querySelector('.js-success-msg').classList.add('hidden');
      }
    }
  });
}

if (!customElements.get('component-pdp-videos')) {
  customElements.define('component-pdp-videos', class ProductVideos extends HTMLElement {
    constructor() {
      super();
      this.state = {
        selectedVideoId: null
      }
      this.props = {
        productUrl: this.dataset.productUrl
      }
    }
    connectedCallback() {
      this.checkSafari();
      if (window.location.href.includes('videoId')) {
        const urlParams = new URLSearchParams(window.location.search);
        const videoId = urlParams.get('videoId');
        if (videoId) {
          this.state.selectedVideoId = videoId;
          this.render(true);
          this.afterRender();
        }
      }
      this.querySelectorAll('.js-pdp-video').forEach(video => {
        video.addEventListener('click', () => {
          this.state.selectedVideoId = video.dataset.mainVideoId;
          this.render();
          this.afterRender();
        });
      });
      this.handleScroll();
    }
    handleScroll() {
      const container = this.querySelector('.js-pdp-videos');
      const next = this.querySelector('.js-videos-next');
      const prev = this.querySelector('.js-videos-prev');
      if (container && next && prev) {
        if (container.scrollWidth > container.clientWidth) {
          next.classList.remove('disabled');
        }
        container.addEventListener('scroll', () => {
          if (container.scrollLeft > 0) {
            prev.classList.remove('disabled');
          } else {
            prev.classList.add('disabled');
          }
          // Hide next button if scrolled to end of slider
          if (container.scrollLeft < (container.scrollWidth - container.offsetWidth - 10)) {
            next.classList.remove('disabled');
          } else {
            next.classList.add('disabled');
          }
        });
        prev.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: -325, behavior: 'smooth' });
        });
        next.addEventListener('click', () => {
          container.scrollBy({ top: 0, left: 325, behavior: 'smooth' });
        });
      }
    }
    checkSafari() {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        this.classList.add("safari");
      }
    }
    render(muted = false) {
      if (this.state.selectedVideoId) {
        const videoTemplate = this.querySelector('.js-template-main-video').content.cloneNode(true);
        const video = this.querySelector(`.js-pdp-video[data-main-video-id="${this.state.selectedVideoId}"]`);
        if (video && video.dataset.mainVideoSrc) {
          videoTemplate.querySelector('.js-main-video-source').src = video.dataset.mainVideoSrc;
          videoTemplate.querySelector('.js-main-video-source').type = video.dataset.mainVideoType;
          if (muted) {
            videoTemplate.querySelector('.js-main-video').muted = true;
          }
          this.querySelector('.js-main-video-container').innerHTML = '';
          this.querySelector('.js-main-video-container').append(videoTemplate);
        }
      }
    }
    afterRender() {
      if (this.querySelector('.js-main-video')) {
        this.querySelector('.js-main-video').load();
      }
      this.querySelector('.js-main-video-close').addEventListener('click', () => {
        this.querySelector('.js-main-video-container').innerHTML = '';
      });
      // Copy link to PDP with video id to clipboard
      this.querySelector('.js-share-video').addEventListener('click', () => {
        navigator.clipboard.writeText(`${this.props.productUrl}?videoId=${this.state.selectedVideoId}`);
        this.querySelector('.js-share-video-confirmation').classList.remove('hidden');
        setTimeout(() => {
          this.querySelector('.js-share-video-confirmation').classList.add('hidden');
        }, 2000);
      });
    }
  });
}
